// Stuck-stream detection for the IDA chat.
//
// #124/#126 root cause: the detector's activity signal was CONTENT-ONLY — it
// only counted assistant text changes as "activity". A long content-silent tool
// batch (e.g. 5 sequential generate_image calls, heartbeats flowing, zero text)
// produced no content change for >2 min, so the detector killed a perfectly
// healthy stream at ~10 min. The fix: treat ANY stream event as activity — tool
// lifecycle, background-job/heartbeat events, job completions, and content
// deltas — by folding them all into one activity signature. When the signature
// changes, the stream is alive.
//
// The 10-minute floor is intentionally NOT raised: with the signal fixed,
// healthy streams keep resetting activity so the 2-minute-silence condition
// never fires for them; the floor only ever combines with genuine silence to
// catch a truly-dead stream (no events of ANY kind for 2 min after 10 min).

/** Minimum loading age before a stream can be considered stuck (10 minutes). */
export const STREAM_STUCK_TIMEOUT_MS = 10 * 60 * 1000;
/** Silence window (no stream events of any kind) that marks a stuck stream. */
export const STREAM_STUCK_SILENCE_MS = 2 * 60 * 1000;
/** How often the detector re-checks while loading (30 seconds). */
export const STREAM_CHECK_INTERVAL_MS = 30 * 1000;

export interface StreamActivityState {
  messages: Array<{ content?: string }>;
  // Serialized whole so ANY per-execution field change counts as activity —
  // not only id/status transitions. If the package bumps a running execution
  // on an inline-tool HEARTBEAT frame (e.g. an elapsed/last-activity field),
  // that surfaces here too, covering a single long content-silent inline tool
  // that never changes status. (If heartbeats never mutate the store at all,
  // no app-side signal can observe them — that needs the package.)
  toolExecutions: readonly unknown[];
  backgroundJobs: readonly unknown[];
  // Monotonic counter bumped by mmagent-chat-ui (>=0.1.83) on every HEARTBEAT
  // frame. This is the authoritative signal for a content-silent, status-stable
  // long inline tool (e.g. a 429-retried generate_image ~146s) that emits only
  // heartbeats — the residual case the store-slice signals above cannot see.
  heartbeatCount: number;
}

/**
 * Build a cheap signature of every observable stream-activity source. A change
 * in this value between checks means a stream event happened (content delta,
 * tool start/end/calling or per-execution update, background-job/heartbeat
 * update, or job completion).
 */
export function computeStreamActivitySignature(
  state: StreamActivityState,
): string {
  const lastMessage = state.messages[state.messages.length - 1];
  const content = lastMessage?.content ?? "";
  const tools = safeStringify(state.toolExecutions);
  const jobs = safeStringify(state.backgroundJobs);
  return `${content}|${tools}|${jobs}|${state.heartbeatCount}`;
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value) ?? "";
  } catch {
    // Circular / non-serializable payloads: fall back to a length marker so the
    // signature is still stable-when-unchanged.
    return Array.isArray(value) ? `len:${value.length}` : "unserializable";
  }
}

/**
 * The kill condition: loading longer than the floor AND no stream event of any
 * kind within the silence window. Both must hold — a stream still emitting tool
 * or heartbeat events (recent activity) is never cancelled, however old it is.
 */
export function isStreamStuck(
  timeSinceStartMs: number,
  timeSinceActivityMs: number,
): boolean {
  return (
    timeSinceStartMs > STREAM_STUCK_TIMEOUT_MS &&
    timeSinceActivityMs > STREAM_STUCK_SILENCE_MS
  );
}

// #5089: a turn can finish and persist on the server in seconds while the
// browser's own stream connection for that turn silently stops delivering
// frames — no error, no close, just nothing more. isStreamStuck cannot help
// here: its 10-minute floor exists for a different problem (a genuinely
// long-running tool batch) and is deliberately not lowered. Without a
// separate, much shorter check, the chat sits on "generating…" until either
// the 10-minute floor eventually fires or the user manually reloads — and a
// reload works instantly because it re-fetches unconditionally, proving the
// server-side data was fine the whole time.
//
// This is a lightweight, non-destructive check: it does not cancel the
// stream or show an interruption notice (isStreamStuck already owns that). It
// only says "it has been quiet long enough that it is worth asking the server
// whether this turn already finished." The caller polls to find out — safe to
// do repeatedly, since merging an unchanged page is a no-op.
/** Three 15-second SSE keepalive intervals without any received bytes. */
export const STREAM_RECONCILE_SILENCE_MS = 45 * 1000;

export function shouldReconcileStalledStream(
  timeSinceActivityMs: number,
): boolean {
  return timeSinceActivityMs > STREAM_RECONCILE_SILENCE_MS;
}
